import React, { useCallback, useEffect, useState } from 'react';
import { Database, Trash2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardBody, Button } from '../ui';
import { supabase } from '../../services/supabaseClient';
import { wipeSampleData } from '../../lib/db';
import toast from '../../lib/toast';
import { useFirm } from '../../contexts/FirmContext';

// SampleDataSection
// ─────────────────────────────────────────────────────────────────────
// Settings → Data tab. Surfaces sample data the firm seeded during
// onboarding so they can remove it permanently when they're ready to
// go fully live. The wipe RPC (migration 117) tags every seeded row
// with is_sample=true and deletes only those rows on demand — real
// firm data is never touched.
//
// Why we needed a dedicated home for this:
//   The onboarding checklist hosted the wipe button before, but it
//   disappears once dismissed or once onboarding is "complete".
//   Firms that loaded sample data and then committed to the product
//   had no in-app way to clean up months later. This section is the
//   permanent home.
//
// What it does:
//   * On mount, counts sample rows across the five tagged tables.
//   * Renders friendly empty-state if zero counts (no need to act).
//   * Renders a count breakdown + Remove button if any counts > 0.
//   * After wipe, refetches counts → snaps back to empty state.

const SAMPLE_TABLES = [
  { table: 'contacts',  label: 'Sample contacts' },
  { table: 'matters',   label: 'Sample case files' },
  { table: 'tasks',     label: 'Sample tasks' },
  { table: 'invoices',  label: 'Sample invoices' },
  { table: 'payments',  label: 'Sample payments' },
];

export default function SampleDataSection() {
  const { firmId } = useFirm();
  const [counts, setCounts] = useState({});   // { contacts: 2, matters: 2, ... }
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  // Fetch counts in parallel using head=true so we don't pay for the
  // rows themselves — PostgREST returns just the count header.
  const refresh = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    const results = await Promise.all(
      SAMPLE_TABLES.map(async ({ table }) => {
        const { count, error } = await supabase
          .from(table)
          .select('id', { count: 'exact', head: true })
          .eq('firm_id', firmId)
          .eq('is_sample', true);
        // Tolerate missing-table / missing-column errors by treating
        // as zero. Migration 117 adds is_sample to all five tables,
        // but older deployments may not have it yet.
        if (error) return [table, 0];
        return [table, count || 0];
      }),
    );
    setCounts(Object.fromEntries(results));
    setLoading(false);
  }, [firmId]);

  useEffect(() => { refresh(); }, [refresh]);

  const total = Object.values(counts).reduce((s, n) => s + (n || 0), 0);

  const handleWipe = async () => {
    if (!window.confirm(
      'Remove all sample data?\n\n' +
      'This deletes the example contacts, case files, tasks, invoices, and payments ' +
      'that were tagged when you loaded sample data. ' +
      'Your real data is untouched — only rows tagged as samples are removed.\n\n' +
      'This action cannot be undone.'
    )) return;
    setWorking(true);
    const { error } = await wipeSampleData();
    setWorking(false);
    if (error) {
      toast.error('Could not remove sample data: ' + (error.message || 'unknown error'));
      return;
    }
    toast.success('Sample data removed. Your real data is untouched.');
    await refresh();
  };

  return (
    <Card>
      <CardHeader
        title="Sample data"
        subtitle="Example contacts, case files, and invoices loaded during onboarding to help you explore the app. Remove them once you're ready to use DocketFlow with real data."
      />
      <CardBody>
        {loading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Checking for sample data…</p>
        ) : total === 0 ? (
          // Empty state — clear, friendly confirmation.
          <div className="flex items-start gap-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-4 py-3">
            <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">No sample data found</p>
              <p className="text-xs text-emerald-800 dark:text-emerald-300 mt-0.5">
                You don't have any sample / example records in your firm. Either you never loaded any, or you've already removed them.
              </p>
            </div>
          </div>
        ) : (
          // Has-data state — counts + wipe button.
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3">
              <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  You have {total} sample record{total === 1 ? '' : 's'} in this firm
                </p>
                <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                  These were created when you loaded sample data during onboarding. They're visible in your lists alongside real records. Tags like "SAMPLE-" in the matter or invoice number can help you identify them.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-200 dark:divide-slate-800">
              {SAMPLE_TABLES.map(({ table, label }) => {
                const n = counts[table] || 0;
                if (n === 0) return null;
                return (
                  <div key={table} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-slate-700 dark:text-slate-300">{label}</span>
                    <span className="font-semibold tabular-nums text-slate-900 dark:text-white">{n}</span>
                  </div>
                );
              })}
            </div>

            <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md">
                Removing sample data is permanent but doesn't affect any real records. Tags like the matter number (<span className="font-mono">SAMPLE-001</span>) make samples easy to spot if you want to review first.
              </p>
              <Button
                variant="danger"
                icon={Trash2}
                onClick={handleWipe}
                disabled={working}
              >
                {working ? 'Removing…' : `Remove all sample data (${total})`}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Database size={14} className="text-slate-500" />
            About sample data
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
            DocketFlow can load a small set of realistic example records — two clients,
            two case files, three tasks, and a sample invoice with a partial payment —
            so new firms can explore the product without having to enter real client data
            first. Every sample record is tagged with <span className="font-mono">is_sample=true</span> in
            the database. The Remove button above deletes only those tagged rows; nothing else.
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
            If you want to <strong>add</strong> sample data, open the onboarding checklist on the
            Dashboard (or the <span className="font-mono">?</span> button → "Re-run the product tour").
          </p>
        </div>
      </CardBody>
    </Card>
  );
}
